import { Link, useLocalSearchParams, useRouter } from 'expo-router';
import { FormProvider, useForm } from 'react-hook-form';
import { useClient, useMutation, useQuery } from 'urql';
import { MemberUpdate } from '@/components/Member/MemberUpdate';
import type { MemberUpdateFormValues } from '@/components/Member/MemberUpdate.types';
import { membershipScope } from '@/components/Membership/Membership.utils';
import Card from '@/components/UI/Card';
import PressableRow from '@/components/UI/PressableRow';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';
import { graphql } from '@/gql/graphql';
import useUserId from '@/hooks/useUserId';
import OrganizationMemberUpdateRoleInput from './components/OrganizationMemberUpdateRoleInput';
import {
  getOrganizationMemberRole,
  getOrganizationMemberSettingsActions,
} from './OrganizationMemberUpdate.utils';

type OrganizationMemberFormValues = Omit<MemberUpdateFormValues, 'memberType'>;

const UpdateOrganizationMember = graphql(/* GraphQL */ `
  mutation UpdateOrganizationMember(
    $organizationId: uuid!
    $personId: String!
    $role: role_enum!
    $shouldUpdateRole: Boolean!
  ) {
    updateRole: update_event_person(
      where: {
        event_id: { _eq: $organizationId }
        person_id: { _eq: $personId }
      }
      _set: { role: $role }
    ) @include(if: $shouldUpdateRole) {
      affected_rows
    }
  }
`);

const GetOrganizationMemberUpdate = graphql(/* GraphQL */ `
  query GetOrganizationMemberUpdateForm(
    $organizationId: uuid!
    $personId: String!
  ) {
    event_by_pk(id: $organizationId) {
      id
      title
      event_type
      event {
        id
        ancestry {
          title
        }
      }
      owner_id
      permissions
    }
    event_person(
      where: {
        event_id: { _eq: $organizationId }
        person_id: { _eq: $personId }
      }
      limit: 1
    ) {
      event_id
      person_id
      role
      person {
        id
        name
        avatar_id
        color
        is_managed
      }
    }
  }
`);

export default function OrganizationMemberUpdate() {
  const client = useClient();
  const [, updateOrganizationMember] = useMutation(UpdateOrganizationMember);
  const { back } = useRouter();
  const userId = useUserId();
  const { organizationId, personId } = useLocalSearchParams<{
    organizationId: string;
    personId: string;
  }>();

  const form = useForm<OrganizationMemberFormValues>({
    defaultValues: async () => {
      const { data: formData } = await client.query(
        GetOrganizationMemberUpdate,
        { organizationId, personId },
        { requestPolicy: 'network-only' },
      );
      const relationship = formData?.event_person[0];

      return {
        role: getOrganizationMemberRole({
          assignedRole: relationship?.role,
          organizationOwnerId: formData?.event_by_pk?.owner_id,
          personId,
        }),
        color: relationship?.person.color,
        avatarId: relationship?.person.avatar_id,
        title: relationship?.person.name,
      };
    },
  });
  const {
    handleSubmit,
    setError,
    clearErrors,
    formState: { dirtyFields, errors, isDirty, isLoading, isSubmitting },
  } = form;
  const [{ data, fetching }] = useQuery({
    query: GetOrganizationMemberUpdate,
    variables: { organizationId, personId },
    requestPolicy: 'network-only',
  });
  const relationship = data?.event_person[0];
  const member = relationship?.person;
  const organization = data?.event_by_pk;
  const contextType =
    organization?.event_type === 'organization' ? 'Organization' : 'Schedule';
  const actions = getOrganizationMemberSettingsActions({
    isManaged: member?.is_managed,
    isOwner: organization?.owner_id === personId,
    isSelf: personId === userId,
    permissions: organization?.permissions,
  });
  const canEdit = actions.editRole;

  const reportSaveError = () =>
    setError('root.save', {
      message: 'Could not save all changes. Refresh and try again.',
    });

  const onSubmit = async (values: OrganizationMemberFormValues) => {
    clearErrors('root.save');
    const shouldUpdateRole = Boolean(actions.editRole && dirtyFields.role);

    if (!relationship || !shouldUpdateRole) {
      return reportSaveError();
    }
    const { data: result, error } = await updateOrganizationMember({
      organizationId,
      personId,
      role: values.role,
      shouldUpdateRole,
    });
    if (
      error ||
      (shouldUpdateRole && result?.updateRole?.affected_rows !== 1)
    ) {
      return reportSaveError();
    }
    back();
  };

  return (
    <FormProvider {...form}>
      <MemberUpdate.Screen
        headerTitle={
          <Box flex minWidth={0}>
            <Text size="xl" numberOfLines={1}>
              {organization ? membershipScope(organization) : contextType}
            </Text>
            <Box direction="horizontal" align="center" space="sm">
              <Icon
                name={
                  contextType === 'Organization'
                    ? 'globe-outline'
                    : 'calendar-outline'
                }
                size="sm"
                variant="typographySecondary"
              />
              <Text size="xs" variant="typographySecondary" numberOfLines={1}>
                {contextType}
              </Text>
            </Box>
          </Box>
        }
        fetching={isLoading || fetching}
      >
        <MemberUpdate.Fields
          masthead={
            <MemberUpdate.Header testID="OrganizationMemberUpdateHeader">
              <MemberUpdate.RoleLabel isManaged={member?.is_managed} />
            </MemberUpdate.Header>
          }
        >
          {actions.editRole ? <OrganizationMemberUpdateRoleInput /> : null}
          {errors.root?.save?.message ? (
            <Text role="alert" variant="error">
              {errors.root.save.message}
            </Text>
          ) : null}
          {relationship && actions.removeMembership ? (
            <Card>
              <Link
                href={{
                  pathname:
                    '/organization/[organizationId]/member/[personId]/edit/delete',
                  params: { organizationId, personId },
                }}
                asChild
              >
                <PressableRow testID="OrganizationMemberRemove">
                  <Icon name="trash-outline" variant="error" />
                  <Text variant="error">Remove from {contextType}</Text>
                </PressableRow>
              </Link>
            </Card>
          ) : null}
        </MemberUpdate.Fields>
        {canEdit ? (
          <MemberUpdate.Save
            disabled={!isDirty || isSubmitting || isLoading || fetching}
            onSave={handleSubmit((values) =>
              onSubmit(values).catch(reportSaveError),
            )}
          />
        ) : null}
      </MemberUpdate.Screen>
    </FormProvider>
  );
}
